/**
 * @file PaymentFormField.tsx
 *
 * Shared field primitives for the Payments & VAT sub-forms, mirroring the
 * Settings form's local helpers: {@link FieldShell} wraps a control with its
 * Gravity label (+ optional "Optional" caption), error, and helper text;
 * {@link SelectControl} renders a native `<select>` styled as a Gravity input
 * with the expand caret (the Gravity `Select` web component isn't drivable under
 * jsdom). Reused by the Connect, Manual payment, and VAT forms.
 */

import type { ChangeEvent, ReactNode } from 'react';
import { Icon } from '@group.one/react-gravity';
import { FormattedMessage } from 'react-intl';
import { adminMessages } from '../i18n/messages';

/** A `{ value, label }` option for a payments `<select>`. */
export interface PaymentSelectOption {
  value: string;
  label: string;
}

/** The labelled wrapper around a single field: label (+ Optional), control, error, helper. */
export function FieldShell({
  id,
  label,
  optional = false,
  error,
  helper,
  children,
}: {
  id: string;
  label: string;
  optional?: boolean;
  error?: string;
  helper?: string;
  children: ReactNode;
}) {
  return (
    <div className="gv-form-option gv-w-full">
      <label className="gv-label" htmlFor={id}>
        <span>{label}</span>
        {optional ? (
          <span className="gv-label-optional">
            <FormattedMessage {...adminMessages.formOptional} />
          </span>
        ) : null}
      </label>
      {children}
      {error ? (
        <span className="gv-text-sm gv-state-critical" role="alert">
          {error}
        </span>
      ) : null}
      {helper ? <p className="gv-text-sm gv-m-0">{helper}</p> : null}
    </div>
  );
}

/** A native `<select>` styled as a Gravity input, with the expand caret. */
export function SelectControl({
  id,
  value,
  testId,
  ariaLabel,
  options,
  placeholder,
  onChange,
}: {
  id: string;
  value: string;
  testId: string;
  ariaLabel: string;
  options: PaymentSelectOption[];
  placeholder?: string;
  onChange: (event: ChangeEvent<HTMLSelectElement>) => void;
}) {
  return (
    <div className="gv-input gv-input-select gv-w-full">
      <select
        id={id}
        value={value}
        data-testid={testId}
        aria-label={ariaLabel}
        onChange={onChange}
      >
        {placeholder !== undefined ? <option value="">{placeholder}</option> : null}
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <Icon name="expand_more" size="small" />
    </div>
  );
}
